/*
 * assets/js/src/reveal.js — příchodové animace sekcí.
 *
 * Návrh na to má dva inline skripty: hero dostane třídu `in` po prvním
 * vykreslení (aby text neposkočil, až doběhne font), pruh lokalit až
 * když vjede do výřezu.
 *
 * ---------------------------------------------------------------------
 * PROČ MODUL PŘIDÁVÁ `za-reveal` NA `<html>`
 * ---------------------------------------------------------------------
 * Pravidla animací dávají prvkům `opacity: 0` a odkrývají je až třídou
 * `in`. Kdyby to CSS platilo bezpodmínečně a modul se nenačetl, obsah by
 * nebyl degradovaný — byl by NEVIDITELNÝ, a nic by to nehlásilo.
 *
 * Proto jsou všechna ta pravidla v `components.css` pod `html.za-reveal`
 * a tuhle třídu přidává TENHLE modul na svém prvním řádku. Bez modulu
 * pravidla neexistují a stránka je prostě vidět.
 *
 * Je to týž vzor jako `za-carousel-ready` u pásu náhledů a vznikl ze
 * stejné lekce (F1 krok 9): podmínka, kterou sází někdo jiný, je
 * podmínka, na kterou se nedá spolehnout.
 *
 * `IntersectionObserver` je v matici podpory celý (Safari 12.1+), ale
 * kontroluje se stejně: bez něj se sekce odkryjí rovnou. Lepší je
 * stránka bez animace než stránka bez obsahu.
 */

/*
 * Sekce, které se odkrývají, a jak.
 *
 * `.chero` je hrdina výpisů, detailů a statických stránek, `.hero`
 * hrdina úvodní stránky (F4). Oba se odkrývají HNED, ne až ve výřezu:
 * stojí nad ohybem a čekat na `IntersectionObserver` by znamenalo
 * prázdnou první obrazovku.
 *
 * Zbytek se odkrývá až při vjezdu do výřezu. Seznam je týž jako v návrhu
 * (`navrh-mix.html`: `.cats, .garance, .form, .loksec, .stats`), jen
 * s NAŠIMI jmény: poptávková sekce je u nás `.formsec` (návrh ji má jako
 * `.form`, což by se u nás trefilo i do formulářových obalů jinde),
 * a `.stats` si odkrývá vlastní modul `stats`, protože kromě třídy `in`
 * ještě natáčí čísla.
 */
const HERO = '.chero, .hero';
/*
 * `.sidehead` (banner „Doporučené akce" v blogu) je v seznamu od 19. 9. 2026.
 * Rozhodnutí majitele: banner se má odkrýt POHYBEM i na mobilu — „aspoň si
 * čtenář uvědomí že je tam tento obsah". Je to vědomá výjimka z pravidla
 * „efekty jen desktop"; tady je smysl upozornit, ne ozdobit.
 *
 * Pozoruje se samotný nadpis, ne jeho sekce: v blogu jsou dva na různých
 * místech stránky a každý má naskočit, až na něj čtenář dojede.
 */
/*
 * Hub „programy nahoře" (dev.16.55, Novoroční detox, majitel 23. 10. 21:45:
 * „vše, co má reveal na HP a vánočním hubu, musí mít i tento hub"): karty
 * programů, scénářů a míst, časté dotazy, karta s body a karta obchodního
 * týmu se odkrývají každá zvlášť. Jen pod `body.hubn` — jinde (vánoční hub,
 * výpisy) žádná z nich `.in` nedostane a nic se nemění.
 */
const LAZY_HUB = '.hubn .plist .prog, .hubn .scard, .hubn .mcard, .hubn .hubfaq .lfaq-item, .hubn .hubdes, .hubn .formsec.zima .person';
const LAZY = `.loksec, .cats, .garance, .formsec, .sidehead, ${LAZY_HUB}`;

/**
 * Odkryje sekci. Voláno buď hned (hero), nebo z observeru (pruh lokalit).
 *
 * @param {Element} el Kořen sekce.
 */
function reveal(el) {
	/*
	 * Bez zápisu, když už třídu má (dev.16.55): `classList.add` zapisuje
	 * atribut i u existující třídy a druhé volání (písma + pojistka
	 * časovačem) pak na iPhonu vypadá jako změna pod prstem.
	 */
	if (!el.classList.contains('in')) {
		el.classList.add('in');
	}
}

/**
 * Spustí modul. Vrací počet odkrytých kořenů — stejný kontrakt jako
 * ostatní moduly, aby se dalo doložit „běží nad tím jedna obsluha".
 *
 * @returns {number}
 */
export function init() {
	const hero = document.querySelector(HERO);
	const lazy = Array.prototype.slice.call(document.querySelectorAll(LAZY));

	if (!hero && !lazy.length) {
		return 0;
	}

	document.documentElement.classList.add('za-reveal');

	let count = 0;

	if (hero) {
		count += 1;
		/*
		 * Až po dokreslení písem. `document.fonts` nemá Safari 14.1 celé,
		 * proto je tu i pojistka časovačem — a obě větve vedou na týž
		 * `reveal()`, takže dvojí zavolání nic nerozbije (`classList.add`
		 * je idempotentní).
		 */
		const go = () => window.requestAnimationFrame(() => reveal(hero));
		if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
			document.fonts.ready.then(go);
			window.setTimeout(go, 700);
		} else {
			go();
		}
	}

	if (!lazy.length) {
		return count;
	}

	if (typeof window.IntersectionObserver !== 'function') {
		for (const el of lazy) {
			reveal(el);
			count += 1;
		}
		return count;
	}

	const observer = new window.IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					reveal(entry.target);
					observer.unobserve(entry.target);
				}
			}
		},
		{ threshold: 0.22 }
	);

	for (const el of lazy) {
		observer.observe(el);
		count += 1;
	}

	return count;
}
